import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  Package,
  ShoppingCart,
  Users,
  ArrowLeftRight,
  FileText,
  ClipboardList,
  CreditCard,
} from "lucide-react";
import { type ActivityItem } from "@/hooks/useDashboardData";
import { formatDistanceToNow } from "date-fns";
import { getPastelColor, getInitial } from "@/features/common/components/createThumbnailColumn";
import { useFrappeFileUrl } from "@/lib/utils/frappeUrl";

const activityTabs = [
  { id: "all", label: "All", icon: null },
  { id: "Item", label: "Products", icon: Package },
  { id: "Sales Order", label: "Orders", icon: ShoppingCart },
  { id: "Customer", label: "Customers", icon: Users },
  { id: "Stock Entry", label: "Stock", icon: ArrowLeftRight },
  { id: "Sales Invoice", label: "Invoices", icon: FileText },
  { id: "Purchase Order", label: "Purchases", icon: ClipboardList },
  { id: "Payment Entry", label: "Payments", icon: CreditCard },
];

interface DashboardRecentActivityProps {
  activities: ActivityItem[];
  title: string;
}

export function DashboardRecentActivity({ activities, title }: DashboardRecentActivityProps) {
  const [activeTab, setActiveTab] = React.useState("all");

  // Hide entire section when no activities exist
  if (activities.length === 0) return null;

  const filteredActivities = activeTab === "all"
    ? activities
    : activities.filter((a) => a.doctype === activeTab);

  const tabsWithData = activityTabs.filter(
    (tab) => tab.id === "all" || activities.some((a) => a.doctype === tab.id)
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.3 }}
    >
      <h2 className="text-sm font-semibold text-foreground mb-3">
        {title}
      </h2>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
        {tabsWithData.map((tab) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          const count = tab.id === "all"
            ? activities.length
            : activities.filter((a) => a.doctype === tab.id).length;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-accent"
              )}
            >
              {Icon && <Icon className="w-4 h-4" />}
              <span>{tab.label}</span>
              <span className={cn(
                "text-xs px-2 py-0.5 rounded-full",
                isActive
                  ? "bg-primary-foreground/20"
                  : "bg-muted"
              )}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Container */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="space-y-1">
          {filteredActivities.slice(0, 8).map((activity, index) => (
            <ActivityRow key={activity.id} activity={activity} index={index} />
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function ActivityRow({ activity, index }: { activity: ActivityItem; index: number }) {
  const toAbsolute = useFrappeFileUrl();
  const [imageFailed, setImageFailed] = React.useState(false);

  const rawPath = activity.doc.imageUrl;
  const rawPathStr = typeof rawPath === "string" ? rawPath.trim() : "";
  const imageUrl = rawPathStr
    ? rawPathStr.startsWith("http://") ||
      rawPathStr.startsWith("https://") ||
      rawPathStr.startsWith("data:")
      ? rawPathStr
      : toAbsolute(rawPathStr)
    : null;

  const showImage = !!imageUrl && !imageFailed;
  const color = getPastelColor(activity.displayName || activity.docname);
  const initial = getInitial(activity.displayName || activity.docname);

  const timeStr = formatDistanceToNow(new Date(activity.timestamp), { addSuffix: false })
    .replace(" minutes", "m")
    .replace(" minute", "m")
    .replace(" hours", "h")
    .replace(" hour", "h")
    .replace(" days", "d")
    .replace(" day", "d")
    .replace("about ", "");

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.03 * index, duration: 0.15 }}
    >
      <Link
        to={activity.href}
        className={cn(
          "flex items-center gap-3 px-3 py-2 rounded transition-all duration-150",
          "hover:bg-accent",
          "active:scale-[0.99]",
          "group"
        )}
      >
        <div className="shrink-0">
          {showImage ? (
            <img
              src={imageUrl}
              alt={activity.displayName}
              className="w-9 h-9 rounded-sm object-cover border border-border/60"
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <div
              className={cn(
                "w-9 h-9 rounded-sm flex items-center justify-center text-sm font-medium border border-border/60",
                color.bg,
                color.text
              )}
            >
              {initial}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-medium text-foreground truncate">
            {activity.displayName}
          </p>
          <p className="text-[11px] text-muted-foreground truncate">
            {activity.action}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {activity.user.avatarUrl ? (
            <img
              src={activity.user.avatarUrl}
              alt={activity.user.fullName}
              title={activity.user.fullName}
              className="w-5 h-5 rounded-full object-cover"
            />
          ) : (
            <div
              className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-[9px] font-medium text-muted-foreground"
              title={activity.user.fullName}
            >
              {activity.user.fullName.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="text-[11px] text-muted-foreground w-8">
            {timeStr}
          </span>
        </div>
      </Link>
    </motion.div>
  );
}
